import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, parseErrorDetail } from "../lib/api";

// R6 password policy mirror — kept in sync with backend/password_policy.py.
const POLICY_HINT =
  "At least 12 characters and must include an upper-case letter, a lower-case letter, a digit, and a symbol.";
const meetsPolicy = (pw) =>
  typeof pw === "string" &&
  pw.length >= 12 &&
  /[A-Z]/.test(pw) &&
  /[a-z]/.test(pw) &&
  /[0-9]/.test(pw) &&
  /[^A-Za-z0-9\s]/.test(pw);

function ResetPasswordModal({ target, onClose, onDone }) {
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!meetsPolicy(newPw)) {
      setError("New password does not meet the policy. " + POLICY_HINT);
      return;
    }
    if (newPw !== confirmPw) {
      setError("New password and confirmation do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/admin/users/${target.id}/reset-password`, {
        new_password: newPw,
        confirm_password: confirmPw,
      });
      onDone({ sessions_revoked: r.data?.sessions_revoked ?? 0 });
    } catch (err) {
      setError(parseErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="apwo-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="rpw-title" data-testid="admin-reset-modal">
      <div className="apwo-modal-card">
        <header className="apwo-modal-head">
          <h3 id="rpw-title" className="apwo-modal-title">Reset password</h3>
          <button type="button" className="apwo-modal-close" onClick={onClose} aria-label="Close" data-testid="admin-reset-close">×</button>
        </header>
        <div className="apwo-modal-body">
          <p className="apwo-modal-target">
            Setting a new password for{" "}
            <strong data-testid="admin-reset-target">{target.username}</strong>{" "}
            <span className="apwo-role-tag">{target.role}</span>
          </p>
          <p className="apwo-muted apwo-modal-note">
            All existing sessions for this account will be signed out. This action is audited (ruling 2026-R18).
          </p>
          <form onSubmit={submit} noValidate autoComplete="off" data-testid="admin-reset-form">
            <label className="apwo-field">
              <span>New password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                minLength={12}
                data-testid="admin-reset-new"
              />
            </label>
            <label className="apwo-field">
              <span>Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                minLength={12}
                data-testid="admin-reset-confirm"
              />
            </label>
            <p className="apwo-muted apwo-modal-policy" data-testid="admin-reset-policy">{POLICY_HINT}</p>
            {error && (
              <div className="apwo-alert-error" role="alert" data-testid="admin-reset-error">
                {error}
              </div>
            )}
            <div className="apwo-modal-actions">
              <button
                type="button"
                className="apwo-btn-secondary"
                onClick={onClose}
                data-testid="admin-reset-cancel"
                disabled={submitting}
              >Cancel</button>
              <button
                type="submit"
                className="apwo-btn-primary"
                disabled={submitting || !newPw || !confirmPw}
                data-testid="admin-reset-submit"
              >{submitting ? "Resetting…" : "Reset password"}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function AdminLanding() {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [resetTarget, setResetTarget] = useState(null);
  const [banner, setBanner] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch (err) {
      setError(parseErrorDetail(err));
    }
  };

  useEffect(() => { load(); }, []);

  const onResetDone = async ({ sessions_revoked }) => {
    setResetTarget(null);
    setBanner({
      kind: "success",
      text: `Password reset. ${sessions_revoked} session${sessions_revoked === 1 ? "" : "s"} signed out.`,
    });
    await load();
    setTimeout(() => setBanner(null), 6000);
  };

  return (
    <div className="apwo-shell apwo-shell--admin">
      <header className="apwo-shell-header">
        <p className="apwo-brand-eyebrow">AUTOPARTSB2B</p>
        <h1 className="apwo-shell-title" data-testid="admin-title">Admin — AP Warehouse Order</h1>
        <div className="apwo-shell-actions">
          <span className="apwo-user-chip" data-testid="admin-user">
            {user?.display_name} <span className="apwo-role-tag">{user?.role}</span>
          </span>
          <button className="apwo-btn-secondary" onClick={logout} data-testid="admin-logout">Sign out</button>
        </div>
      </header>
      <main className="apwo-shell-main">
        <nav className="apwo-nav-tabs" aria-label="Admin sections">
          <a href="/admin" className="apwo-nav-tab active" data-testid="admin-nav-users">Users & Overview</a>
          <a href="/admin/orders" className="apwo-nav-tab" data-testid="admin-nav-orders">Orders Inbox</a>
          <a href="/admin/catalogue" className="apwo-nav-tab" data-testid="admin-nav-catalogue">Catalogue</a>
          <a href="/admin/backorders" className="apwo-nav-tab" data-testid="admin-nav-backorders">Backorders</a>
        </nav>
        <section className="apwo-panel">
          <h2>Owner Admin dashboard</h2>
          <p>Full admin screens (fulfilment, refunds, Reward ledger, catalogue import, audit) arrive in a later phase. This Preview shell exposes the Users register and the R4 Orders Inbox.</p>
          <div className="apwo-empty-actions" style={{ justifyContent: "flex-start" }}>
            <button className="apwo-btn-primary" onClick={() => (window.location.href = "/admin/orders")} data-testid="admin-open-orders-inbox">
              Open Orders Inbox →
            </button>
          </div>
        </section>
        <section className="apwo-panel">
          <h3>Users</h3>
          {banner && (
            <div className="apwo-alert-success" role="status" data-testid="admin-reset-success">{banner.text}</div>
          )}
          {error && <div className="apwo-alert-error" role="alert">{error}</div>}
          {!users && !error && <p className="apwo-muted">Loading…</p>}
          {users && (
            <table className="apwo-table" data-testid="admin-users-table">
              <thead>
                <tr>
                  <th>Username</th><th>Display name</th><th>Role</th><th>MFA</th><th>Suspended</th><th>Last login</th><th className="apwo-th-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                    <td>{u.username}</td>
                    <td>{u.display_name}</td>
                    <td><span className="apwo-role-tag">{u.role}</span></td>
                    <td>{u.mfa_enabled ? "Yes" : "No"}</td>
                    <td>{u.suspended ? "Yes" : "No"}</td>
                    <td>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}</td>
                    <td className="apwo-th-right">
                      {u.role !== "OWNER_ADMIN" ? (
                        <button
                          type="button"
                          className="apwo-btn-secondary apwo-btn-sm"
                          onClick={() => setResetTarget(u)}
                          data-testid={`admin-reset-btn-${u.id}`}
                        >Reset password</button>
                      ) : (
                        <span className="apwo-muted apwo-cell-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>
      <footer className="apwo-shell-footer">
        AUTOPARTSB2B &middot; S1 Asia Pacific Traders Pty Ltd &middot; ABN 69 650 366 091
      </footer>

      {resetTarget && (
        <ResetPasswordModal
          target={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={onResetDone}
        />
      )}
    </div>
  );
}
